import * as React from "react";
import { ReactorConfiguration } from "@app/arc";
import { History } from "history";
import { ProjectRunConfigurationStatus, ProjectRunStatusEnum } from "@app/projects/enumerators";
import { BreadcrumbTemplateEnum, breadcrumbTemplates } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import { ProjectActionMenuModel, ResourceShareLocationState } from "@app/projects/network-assessment/projectActionMenuTypes";
import { JSX } from "react";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import ProjectService from "@app/projects/projectService";
import ZipProjectService from "@app/projects/zipProjectService";
import ProjectTileData from "@app/projects/projectTileData";
import { ResourceStatus, Resource, ResourceType, FilteredResource } from "@app/resources";
import ResourceService from "@app/resources/resourceService";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";

const handleArchiveConfirm = async (isArchived: boolean, resourceId: number, onArchived?: (resourceId: number) => void): Promise<void> => {
  const resourceService = new ResourceService();
  if (isArchived) {
    await resourceService.updateResourceArchiveStatus(resourceId, ResourceStatus.Active);
  } else {
    await resourceService.updateResourceArchiveStatus(resourceId, ResourceStatus.Archived);
  }

  if (onArchived) {
    onArchived(resourceId);
  }
};

const handleHiddenChange = async (resourceId: number, onHidden?: (resourceId: number) => void): Promise<void> => {
  const resourceService = new ResourceService();
  await resourceService.updateResourceHiddenStatus(resourceId);

  if (onHidden) {
    onHidden(resourceId);
  }
};

const handleClone = (history: History<unknown>, resourceId: number): void => {
  history.push("/projects/" + resourceId + "/clone");
};

const handleCancelQueuedProject = async (projectId: number): Promise<void> => {
  const projectService = new ProjectService();
  await projectService.cancelQueuedProject(projectId);
};

const handlePublishProject = async (
  projectId: number,
  isZipAnalysis: boolean,
  comment: string,
  onPublished?: () => void,
): Promise<void> => {
  if (isZipAnalysis) {
    const zipProjectService = new ZipProjectService();
    await zipProjectService.publishZipProject(projectId, comment);
  } else {
    const projectService = new ProjectService();
    await projectService.publishProject(projectId, comment);
  }
  if (onPublished) {
    onPublished();
  }
};

const handleUpdateSummary = async (
  onUpdateProjectOptions: (summary: ProjectTileData) => void,
  projectResource: Resource,
  filteredResource: FilteredResource,
): Promise<void> => {
  const projectService = new ProjectService();
  //re-retrieve the summary results since the project was updated
  const projectDashboardModel = await projectService.projectTileResults([filteredResource],
    projectResource.isArchived ? ResourceStatus.Archived : ResourceStatus.Active);

  if (projectDashboardModel
    && projectDashboardModel.projectTileAggregates
    && projectDashboardModel.projectTileAggregates.length === 1) {
    //pass back to the Home component to refresh its data and re-render
    onUpdateProjectOptions(projectDashboardModel.projectTileAggregates[0]);
  }
};

const buildReactorConfig = (projectTileData: ProjectTileData, useCompanionAdequacy?: boolean): ReactorConfiguration | undefined => {
  // TODO: Temporary until re-aggregation is done on the fly for projection and specialty group exception changes
  // TODO: Join with ProjectTile.tsx's Composite implementation.
  if (projectTileData) {
    const r = projectTileData;
    const a = useCompanionAdequacy ? r.companionAdequacy : r.adequacy;
    const showAttestation = r.showAttestation &&
      ((a && a.recentlyAttestedUniqueNPIPercent !== undefined) ||
        (r && r.accuracy));

    //account for accuracy-only projects when checking for attestation
    const attestationPercent = a && a.recentlyAttestedUniqueNPIPercent
      ? a.recentlyAttestedUniqueNPIPercent
      : r && r.accuracy && r.accuracy.recentlyAttestedPercent
        ? r.accuracy.recentlyAttestedPercent
        : undefined;

    const conf: ReactorConfiguration = {
      accuracy: null,
      adequacy: a && {
        blue: a.percentPassByException || 0,
        green: a.percentPassBoth || 0,
        index: a.adequacyIndex !== null && a.adequacyIndex !== undefined ? a.adequacyIndex : "N/A",
        projected: a.projectedPercentPassBoth || null,
        projectedIndex: a.projectedAdequacyIndex || null,
        red: a.percentPassNone || 0,
        yellow: ((a.percentPassAccessOnly || 0) + a.percentPassServicingOnly) || 0,
      } || null,
      attestation: showAttestation && ({
        green: attestationPercent || 0,
        index: attestationPercent || 0,
        isPercent: true,
        red: 0,
        yellow: 0,
      }) || null,
      composite: null,
      compositeOverride: "adequacy",
    };

    return conf;
  }

  return undefined;
};

interface ProjectTileState {
  projectResource: Resource;
  sidePanelArchiveWarningVisible: boolean;
  summary: ProjectTileData;
  triggerHide: () => void;
  triggerOpen: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

const renderProjectStatus = (projectData: ProjectTileData, isTile?: boolean, userRoles?: string[]): JSX.Element => {
  if (!projectData) {
    return;
  }

  const runConfigurationStatus = projectData.runConfigurationStatus
    ? projectData.runConfigurationStatus
    : ProjectRunConfigurationStatus.Created;

  let statusText: string;
  let statusTipText: string;
  let statusColor: string;
  let statusQesIcon: string;
  let extraIcon: JSX.Element;

  switch (runConfigurationStatus) {
    case ProjectRunConfigurationStatus.Created:
      statusText = "Ready";
      statusTipText = "Project Valid and Ready to Run";
      statusColor = "text-success";
      statusQesIcon = QesIcons.CheckCircle;
      break;

    case ProjectRunConfigurationStatus.Succeeded:
      statusText = "Complete";
      statusTipText = "Project Completed Successfully";
      statusColor = "text-success";
      statusQesIcon = QesIcons.CheckCircle;

      const projectionProcessor = userRoles?.includes(Roles.PROJECTION_PROCESSOR);
      const termAdmin = userRoles?.includes(Roles.TERM_PROVIDER_ADMIN);
      const exceptionManager = userRoles?.includes(Roles.EXCEPTION_MANAGER);
      const projectHasStatus = !projectData.isRunning && projectData.runConfigurationStatus !== ProjectRunConfigurationStatus.Failed;
      const showTermMessage = termAdmin && projectData.hasUnprocessedProviderTerminations && projectHasStatus;
      const showProjectionMessage = projectionProcessor && projectData.hasUnprocessedProjectionProviders && projectHasStatus;
      const showUnprocessedSpecialtyGroupExceptions = exceptionManager && projectData.hasUnprocessedSpecialtyGroupExceptions && projectHasStatus;

      const messageArray = [];
      const roleArray = [];

      if (showTermMessage) {
        messageArray.push("Unprocessed Provider Terminations");
        roleArray.push(Roles.TERM_PROVIDER_ADMIN);
      }
      if (showProjectionMessage) {
        messageArray.push("Unprocessed Targeted or Excluded Providers");
        roleArray.push(Roles.PROJECTION_PROCESSOR);
      }
      if (showUnprocessedSpecialtyGroupExceptions) {
        messageArray.push("Unprocessed Specialty Group Exceptions");
        roleArray.push(Roles.EXCEPTION_MANAGER);
      }

      extraIcon = isTile && projectHasStatus &&
        (showTermMessage || showProjectionMessage || showUnprocessedSpecialtyGroupExceptions ?
          <RoleRestricted allowedRoles={roleArray}>
            <TooltipWrapper text={messageArray.join(" and ")}>
              <span className="text-yellow project-tile__status-icons"><QesIcon iconName={QesIcons.AlertTriangle} description="Warning" width="15" height="15" /></span>
            </TooltipWrapper>
          </RoleRestricted> :
          null);

      break;

    case ProjectRunConfigurationStatus.Queued:
      statusText = "Queued";
      statusColor = "text-info";
      statusQesIcon = QesIcons.LoadingV1;
      statusTipText = "Project Queued Up to Run";
      break;

    case ProjectRunConfigurationStatus.Analyzing:
    case ProjectRunConfigurationStatus.Aggregating:
    case (ProjectRunStatusEnum.Running as unknown as ProjectRunConfigurationStatus):
      statusText = "Running";
      statusTipText = "Project Currently Running";
      statusColor = "text-info";
      statusQesIcon = QesIcons.LoadingV1;
      break;

    case ProjectRunConfigurationStatus.Canceled:
      statusText = "Canceled";
      statusColor = "text-danger";
      statusQesIcon = QesIcons.XCircle;
      statusTipText = "Project Canceled While Running";
      break;

    case ProjectRunConfigurationStatus.Failed:
      statusText = "Failed";
      statusTipText = "Project Encountered an Error";
      statusColor = "text-danger";
      statusQesIcon = QesIcons.XCircle;
      break;

    case ProjectRunConfigurationStatus.FailedProviderChanges:
      statusText = "Failed";
      statusTipText = "Project Encountered an Error";
      statusColor = "text-danger";
      statusQesIcon = QesIcons.XCircle;
      break;

    case ProjectRunConfigurationStatus.QueuedProviderChanges:
      statusText = "Queued";
      statusTipText = "Project Queued to Process Provider Changes";
      statusColor = "text-info";
      statusQesIcon = QesIcons.LoadingV1;
      break;

    case ProjectRunConfigurationStatus.ProcessingProviderChanges:
      statusText = "Running";
      statusTipText = "Project Currently Processing Provider Changes";
      statusColor = "text-info";
      statusQesIcon = QesIcons.LoadingV1;
      break;

    case ProjectRunConfigurationStatus.QueuedSpecialtyGroupExceptionAnalysis:
      statusText = "Queued";
      statusTipText = "Project Queued to Update Results with Specialty Group Exception Change";
      statusColor = "text-info";
      statusQesIcon = QesIcons.LoadingV1;
      break;

    case ProjectRunConfigurationStatus.ProcessingSpecialtyGroupExceptionAnalysis:
      statusText = "Running";
      statusTipText = "Project Currently Updating Results with Specialty Group Exception Change";
      statusColor = "text-info";
      statusQesIcon = QesIcons.LoadingV1;
      break;

    case ProjectRunConfigurationStatus.QueuedProspectMarketNcp:
      statusText = "Queued";
      statusTipText = "Project Queued to Process Next Closest Providers";
      statusColor = "text-info";
      statusQesIcon = QesIcons.LoadingV1;
      break;

    case ProjectRunConfigurationStatus.ProcessingProspectMarketNcp:
      statusText = "Running";
      statusTipText = "Project Currently Processing Next Closest Providers";
      statusColor = "text-info";
      statusQesIcon = QesIcons.LoadingV1;
      break;

    case ProjectRunConfigurationStatus.FailedProspectMarketNcp:
      statusText = "Failed";
      statusTipText = "Project Encountered an Error";
      statusColor = "text-danger";
      statusQesIcon = QesIcons.XCircle;
      break;

    case ProjectRunConfigurationStatus.CanceledProspectMarketNcp:
      statusText = "Canceled";
      statusTipText = "Project Canceled While Processing Next Closest Providers";
      statusColor = "text-danger";
      statusQesIcon = QesIcons.XCircle;
      break;
  }

  return (
    <p className={`${statusColor} mb-0`} data-bs-toggle="tooltip" title={statusTipText}>
      {projectData.isReadOnly && <span className="text-dark me-2 project-tile__status-icons"><QesIcon iconName={QesIcons.GlassesV2} description="Read-Only Mode" width="15" height="15" /></span> }
      <span className="project-tile__status-icons"><QesIcon iconName={statusQesIcon} isDecorative className={statusQesIcon == QesIcons.LoadingV1 ? "qes-icon-spin" : ""} width="13" /></span>
      &nbsp;{statusText}&nbsp;
      {extraIcon}
      {
        (projectData.runConfigurationStatus &&
          projectData.runConfigurationStatus === ProjectRunConfigurationStatus.Succeeded &&
          projectData.hasUnpublishedResults || null) &&
        <span className="fs18 text-danger project-tile__status-icons"><QesIcon iconName={QesIcons.Eye} description="Contains Unpublished data" width="15" height="15"/></span>
      }
    </p>
  );
};

// Classic-side translators into the shared action menu's view-agnostic shape — used by ProjectTile and
// ProjectTable so they can render SharedProjectActionMenu directly (no per-view component wrapper).
const toClassicActionMenuModel = (resource: Resource, projectTileData: ProjectTileData): ProjectActionMenuModel => ({
  dataReady: !!projectTileData,
  isArchived: resource.isArchived,
  isPublishable: !!projectTileData &&
    projectTileData.runConfigurationStatus === ProjectRunConfigurationStatus.Succeeded &&
    projectTileData.hasUnpublishedResults,
  isQueued: !!projectTileData && projectTileData.runConfigurationStatus === ProjectRunConfigurationStatus.Queued,
  isReadOnly: !!projectTileData && projectTileData.isReadOnly,
  isRunning: !!projectTileData && projectTileData.isRunning,
  isZipAnalysis: resource.resourceType === ResourceType.ZipAnalysisProject,
  name: resource.name,
  projectId: resource.resourceId,
});

const buildClassicShareState = (resource: Resource, pathname: string): ResourceShareLocationState => ({
  breadcrumbItems: [
    ...breadcrumbTemplates[BreadcrumbTemplateEnum.NetworkAssessment],
    {
      link: pathname,
      text: resource.name,
    },
  ],
  isSingleResource: true,
  resourceDescription: "Project",
  resourceId: resource.resourceId,
  resourceTypes: [ResourceType.Project],
});

export {
  buildClassicShareState,
  buildReactorConfig,
  handleArchiveConfirm,
  handleCancelQueuedProject,
  handleClone,
  handleHiddenChange,
  handlePublishProject,
  handleUpdateSummary,
  ProjectTileState,
  renderProjectStatus,
  toClassicActionMenuModel,
};
